import { useEffect, useRef, useState } from 'react';

import type { SortDirection, SortParams } from '@klareospace/pharos-lib-types';

import { DEFAULT_TABLE_PAGE_SIZE, SEARCH_DEBOUNCE_MS } from '../constants';
import { buildSortParams } from '../lib/pagination-helpers';
import { useDebouncedValue } from './use-debounced-value';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseSearchPageStateConfig {
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number;
  /** Number of items per page (default: 10). Controlled — a changed value resets the current page to 1. */
  pageSize?: number;
}

export interface UseSearchPageStateReturn<TSortColumn extends string> {
  /** Current page (accounts for search-driven reset) */
  currentPage: number;
  /** The debounced, trimmed search string — use this in queries (undefined when empty) */
  debouncedSearch: string | undefined;
  /** Handle page change */
  onPageChange: (newPage: number) => void;
  /** Handle search input change */
  onSearchChange: (value: string) => void;
  /** Handle sort change — also resets page to 1 */
  onSort: (sortBy: TSortColumn, sortDirection: SortDirection | null) => void;
  /** Page size constant */
  pageSize: number;
  /** Reset page to 1 — call from custom filter handlers */
  resetPage: () => void;
  /** Current raw search input value (unprocessed) */
  searchValue: string;
  /** Current sort params (undefined if no sort active) */
  sort: SortParams<TSortColumn> | undefined;
  /** Current sort column */
  sortBy: TSortColumn | undefined;
  /** Current sort direction */
  sortDirection: SortDirection | undefined;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useSearchPageState<TSortColumn extends string>(
  config: UseSearchPageStateConfig = {},
): UseSearchPageStateReturn<TSortColumn> {
  const { debounceMs = SEARCH_DEBOUNCE_MS, pageSize = DEFAULT_TABLE_PAGE_SIZE } = config;

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [sort, setSort] = useState<SortParams<TSortColumn> | undefined>(undefined);

  const debouncedSearch = useDebouncedValue(searchInput, debounceMs);
  const trimmedSearch = debouncedSearch.trim();
  const prevTrimmedSearchRef = useRef(trimmedSearch);
  const prevPageSizeRef = useRef(pageSize);

  // Reset to page 1 synchronously on search/pageSize change — avoids a wasted fetch with the stale page number.
  let currentPage = page;
  if (prevTrimmedSearchRef.current !== trimmedSearch || prevPageSizeRef.current !== pageSize) {
    currentPage = 1;
  }

  useEffect(() => {
    if (prevTrimmedSearchRef.current !== trimmedSearch) {
      prevTrimmedSearchRef.current = trimmedSearch;
      setPage(1);
    }
  }, [trimmedSearch]);

  useEffect(() => {
    if (prevPageSizeRef.current !== pageSize) {
      prevPageSizeRef.current = pageSize;
      setPage(1);
    }
  }, [pageSize]);

  function handleSort(sortBy: TSortColumn, sortDirection: SortDirection | null): void {
    setSort(buildSortParams(sortBy, sortDirection));
    setPage(1);
  }

  function handlePageChange(newPage: number): void {
    setPage(newPage);
  }

  function resetPage(): void {
    setPage(1);
  }

  return {
    currentPage,
    debouncedSearch: trimmedSearch || undefined,
    onPageChange: handlePageChange,
    onSearchChange: setSearchInput,
    onSort: handleSort,
    pageSize,
    resetPage,
    searchValue: searchInput,
    sort,
    sortBy: sort?.sortBy,
    sortDirection: sort?.sortDirection,
  };
}
